import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import AvatarChip from '../../../../shared/avatar-chip';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import { SignatureTemplateFormValues } from '../signature-templates-form-page';
import { SectionHeader } from '../../../../shared/section-header';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';
import Radio from '@mui/material/Radio';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';

interface StepSignatureTemplateBasicInfoProps {
  control: Control<SignatureTemplateFormValues>;
  watch: UseFormWatch<SignatureTemplateFormValues>;
  setValue: UseFormSetValue<SignatureTemplateFormValues>;
  errors: FieldErrors<SignatureTemplateFormValues>;
}

export default function StepSignatureTemplateBasicInfo({
  control,
  watch,
  setValue,
  errors,
}: StepSignatureTemplateBasicInfoProps) {
  const manualRun = watch('manualRun') || false;
  const scheduled = watch('scheduled');
  const [runOnPrimaryAlias, runOnAliases, runOnDefaultAlias] = watch([
    'runOnPrimaryAlias',
    'runOnAliases',
    'runOnDefaultAlias',
  ]);

  const radioValue = runOnPrimaryAlias
    ? 'runOnPrimaryAlias'
    : runOnAliases
    ? 'runOnAliases'
    : runOnDefaultAlias
    ? 'runOnDefaultAlias'
    : '';

  const autocompleteKey = useRef(new Date().getTime().toString());
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const emailsToNotify = watch('emailsToNotify');
  const notifyViaEmail = watch('notifyViaEmail');

  const handleCheckboxChange = (selectedValue: string) => {
    setValue('runOnPrimaryAlias', selectedValue === 'runOnPrimaryAlias');
    setValue('runOnDefaultAlias', selectedValue === 'runOnDefaultAlias');
    setValue('runOnAliases', selectedValue === 'runOnAliases');
  };

  return (
    <Box gap={2} width={'100%'}>
      <Stack gap={1}>
        <SectionHeader description="Please provide a signature template name and an optional description.">
          Template Information
        </SectionHeader>
        <Grid container rowSpacing={1} columnSpacing={2} size={{ xs: 10 }}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="templateName"
              control={control}
              render={({ field }) => (
                <TextField
                  required
                  autoComplete="off"
                  label="Template Name"
                  sx={{ width: '100%' }}
                  {...field}
                  error={!!errors.templateName}
                  helperText={errors.templateName?.message || ' '}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoComplete="off"
                  inputProps={{
                    id: 'description',
                  }}
                  InputLabelProps={{
                    htmlFor: 'description',
                  }}
                  label="Description"
                  sx={{ width: '100%' }}
                />
              )}
            />
          </Grid>
          <Stack flexDirection="column">
            <Grid size={{ xs: 12 }}>
              <Controller
                name="manualRun"
                control={control}
                render={({ field: props }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...props}
                        checked={props.value}
                        onChange={(e) => {
                          props.onChange(e.target.checked);
                          if (!e.target.checked && !scheduled) {
                            setValue('scheduled', true);
                          }
                        }}
                      />
                    }
                    label="Manual Run"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="scheduled"
                control={control}
                render={({ field: props }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...props}
                        checked={props.value}
                        onChange={(e) => {
                          props.onChange(e.target.checked);
                          if (!e.target.checked && !manualRun) {
                            setValue('manualRun', true);
                          }
                        }}
                      />
                    }
                    label="Set Schedule"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl
                sx={{ mt: 2 }}
                error={!!errors.runOnPrimaryAlias && radioValue === ''}
                variant="standard"
              >
                <FormLabel>Send As Option</FormLabel>
                <RadioGroup
                  aria-labelledby="radio-buttons-group-label"
                  defaultValue="User"
                  name="radio-buttons-group"
                  value={radioValue}
                  onChange={(e) => {
                    handleCheckboxChange(e.target.value);
                  }}
                >
                  <FormControlLabel
                    value="runOnPrimaryAlias"
                    control={<Radio />}
                    label="Apply to Only Primary Send As Address"
                    sx={{ width: 'fit-content' }}
                  />
                  <FormControlLabel
                    value="runOnDefaultAlias"
                    control={<Radio />}
                    label="Apply to Both Primary and Default Send As Addresses"
                    sx={{ width: 'fit-content' }}
                  />
                  <FormControlLabel
                    value="runOnAliases"
                    control={<Radio />}
                    label="Apply to All Send As Addresses"
                    sx={{ width: 'fit-content' }}
                  />
                  {radioValue === 'runOnAliases' && (
                    <Alert severity="warning">
                      This will cause all users chosen to have their "Send As"
                      signatures changed to this template.
                    </Alert>
                  )}
                </RadioGroup>
                <FormHelperText error>
                  {errors.runOnPrimaryAlias && radioValue === ''
                    ? errors.runOnPrimaryAlias.message
                    : ' '}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Stack>
          <Grid size={{ xs: 12 }}>
            <Controller
              name="useSendAsAccountForTemplate"
              control={control}
              render={({ field: props }) => (
                <Stack flexDirection="column" sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...props}
                        checked={props.value}
                        onChange={(e) => {
                          props.onChange(e.target.checked);
                        }}
                      />
                    }
                    label="Use send as account for signature template"
                    sx={{ width: 'fit-content' }}
                  />
                  <FormHelperText sx={{ pl: 4, mt: 0 }}>
                    This will cause the signature to use the 'Send As' account
                    for any templated fields.
                  </FormHelperText>
                </Stack>
              )}
            />
          </Grid>
        </Grid>
      </Stack>
      <Stack gap={1}>
        <SectionHeader
          sx={{ mt: 2 }}
          description="Optionally add any specific users/groups that you would like to send an email notification to when the signature template has finished running."
        >
          Email Notifications
        </SectionHeader>
        <Stack flexDirection="row" gap={1}>
          <Controller
            name="emailsToNotify"
            control={control}
            render={() => (
              <Box sx={{ width: 400 }}>
                <SharedAutocomplete
                  sources={['users', 'groups']}
                  autocompleteValue={autoCompleteResponse}
                  setAutoCompleteValue={(value: string) => {
                    if (!value) return;
                    if (emailsToNotify?.includes(value)) {
                      return;
                    }
                    if (!notifyViaEmail) {
                      setValue('notifyViaEmail', true);
                    }

                    setValue('emailsToNotify', [
                      ...(emailsToNotify ?? []),
                      value,
                    ]);
                    autocompleteKey.current = new Date().getTime().toString();
                    setAutoCompleteResponse('');
                  }}
                />
              </Box>
            )}
          />
        </Stack>

        <Stack
          flexDirection="row"
          gap={1}
          flexWrap="wrap"
          sx={{ mt: 1, mb: 2 }}
        >
          {emailsToNotify?.map((user) => (
            <AvatarChip
              key={user}
              label={user}
              type="Unknown"
              email={user}
              onDelete={() => {
                const updated = emailsToNotify.filter((u) => u !== user);
                setValue('emailsToNotify', updated);

                if (notifyViaEmail && updated.length === 0) {
                  setValue('notifyViaEmail', false);
                }
              }}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}
